import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { RegistrationService } from '../../registration.service';
import { GlobalSearchStakeholderComponent } from '../../../../shared/components/global-search-stakeholder/global-search-stakeholder.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { UrlParamsService } from 'src/app/shared/services/url-params.service';
import { MinProjectDTO } from 'src/app/features/subscription/types/MinProjectDTO';
import { RegisterForSurveyRequestDTO } from '../../types/RegisterForSurveyRequestDTO';

@Component({
  selector: 'app-survey-123-registration',
  imports: [
    CommonModule,
    GlobalSearchStakeholderComponent,
    MatCheckbox,
    ReactiveFormsModule,
    MatIconModule,
  ],
  templateUrl: './survey-123-registration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './survey-123-registration.component.scss',
})
export class Survey123RegistrationComponent implements OnInit {
  private subscription: Subscription | null = null;
  projectInfo: MinProjectDTO | null = null;
  isStakeholderSearchValid: boolean = false;
  onStakeholderSearch: number = 0;
  registrationForm: FormGroup;

  constructor(
    private registrationService: RegistrationService,
    private urlParamsService: UrlParamsService,
    private fb: FormBuilder
  ) {
    this.registrationForm = this.fb.group({
      stakeGuid: ['', Validators.required],
      projectGuid: ['', Validators.required],
      s123Id: ['', Validators.required],
      isAnonymous: [false],
    });
  }

  ngOnInit(): void {
    this.subscription = this.registrationService.projectInfo$.subscribe({
      next: (projectInfo) => {
        this.projectInfo = projectInfo;
        this.registrationForm.get('projectGuid')?.setValue(this.projectInfo?.ProjectGuid);
      },
      error: (error) => {
        console.error('Error fetching project info:', error);
      },
    });
    this.registrationForm.get('s123Id')?.setValue(this.urlParamsService.s123_id);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  async onRegisterForSurvey(): Promise<void> {
    if (!this.registrationForm.get('isAnonymous')?.value) {
      // Emits a value to the stake search component to run the search API call
      this.onStakeholderSearch++;
    } else {
      try {
        const anonStakeGuid = await this.getAnonymousStakeholderGuid();
        if (this.projectInfo) {
          const registrant: RegisterForSurveyRequestDTO = {
            stakeGuid: anonStakeGuid,
            projectGuid: this.registrationForm.value.projectGuid,
            s123Id: this.registrationForm.value.s123Id,
          };
          this.registerForSurvey(registrant);
        }
      } catch (error) {
        console.error('Error during registration:', error);
      }
    }
  }

  isSubmitDisabled(): boolean {
    return !this.registrationForm.get('isAnonymous')?.value && !this.isStakeholderSearchValid;
  }

  handleStakeholderSearchResult(event: { status: string; data: any; formValues: any }): void {
    switch (event.status) {
      case 'success':
        if (event.data && event.data.StakeGuid) {
          this.registrationForm.get('stakeGuid')?.setValue(event.data.StakeGuid);

          this.registerForSurvey({
            stakeGuid: event.data.StakeGuid,
            projectGuid: this.registrationForm.get('projectGuid')?.value,
            s123Id: this.registrationForm.get('s123Id')?.value,
          });
        } else {
          this.urlParamsService.navigateWithParams('/registration/new-stakeholder', {
            LastName: event.formValues.LastName,
            Mail1ZipCode: event.formValues.Mail1ZipCode,
            StakeEmail: event.formValues.StakeEmail,
            SubscribeSourceId: 5,
            isEdit: false,
            backUrl: '/registration/survey123',
            registrationMode: 'survey123',
          });
        }
        break;
      case 'error':
        console.error('Error fetching stakeholder:', event.data);
        this.urlParamsService.navigateToErrorPage('Error searching for stakeholder.');
        break;
      default:
        break;
    }
  }

  handleFormValidity(event: boolean): void {
    this.isStakeholderSearchValid = event;
  }

  private registerForSurvey(registrant: RegisterForSurveyRequestDTO): void {
    this.registrationService.registerForSurvey(registrant).subscribe({
      next: (response) => {
        this.urlParamsService.navigateWithParams('/registration/survey123/confirmation', {
          survey123Url: response,
        });
      },
      error: (error) => {
        console.error('Error registering stakeholder for survey:', error);
        this.onStakeholderSearch++;
        this.urlParamsService.navigateToErrorPage('Error registering for survey.');
      },
    });
  }

  private async getAnonymousStakeholderGuid(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.registrationService.getAnonymousStakeholder().subscribe({
        next: (response) => resolve(response.StakeGuid),
        error: (error) => {
          console.error('Error fetching anonymous stakeholder:', error);
          reject('');
          this.urlParamsService.navigateToErrorPage('An error occurred.');
        },
      });
    });
  }
}
